/** ApexCharts category x-axis zoom range (0-based inclusive indices). */
export type CategoryZoomRange = { min: number; max: number } | null;

type ApexZoomGlobals = {
  minX?: number;
  maxX?: number;
  dataPoints?: number;
};

type ApexZoomChartContext = {
  w?: { globals?: ApexZoomGlobals };
};

/**
 * Derive visible category indices after zoom (selection or mouse wheel).
 * Prefers chart globals — wheel zoom on category axes is more reliable there than xaxis min/max.
 */
export function categoryZoomRangeFromChart(
  chartContext: unknown,
  xaxis: { min?: number; max?: number } | undefined,
  dataPointCount: number
): CategoryZoomRange {
  const globals = (chartContext as ApexZoomChartContext)?.w?.globals;

  let min: number | undefined;
  let max: number | undefined;

  if (globals && Number.isFinite(globals.minX) && Number.isFinite(globals.maxX)) {
    min = Math.floor(Number(globals.minX));
    max = Math.ceil(Number(globals.maxX));
  } else if (xaxis?.min != null && xaxis?.max != null) {
    min = Math.floor(Number(xaxis.min));
    max = Math.ceil(Number(xaxis.max));
  } else {
    return null;
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (min > max) [min, max] = [max, min];

  min = Math.max(0, min);
  if (dataPointCount > 0) {
    max = Math.min(dataPointCount - 1, max);
  } else {
    return null;
  }

  if (dataPointCount > 0 && min <= 0 && max >= dataPointCount - 1) {
    return null;
  }

  return { min, max };
}

/** @deprecated Prefer categoryZoomRangeFromChart with chart context and data point count. */
export function categoryZoomRangeFromXAxis(
  xaxis: { min?: number; max?: number } | undefined
): CategoryZoomRange {
  if (xaxis?.min == null || xaxis?.max == null) return null;
  const min = Math.max(0, Math.floor(Number(xaxis.min)));
  const max = Math.ceil(Number(xaxis.max));
  if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) return null;
  return { min, max };
}

/** Slice series values for the zoomed category range (inclusive max index). */
export function sliceByCategoryZoom<T>(values: T[], zoom: CategoryZoomRange): T[] {
  if (!zoom || values.length === 0) return values;
  const start = Math.max(0, Math.min(zoom.min, values.length - 1));
  const end = Math.min(values.length, zoom.max + 1);
  if (end <= start) return values;
  return values.slice(start, end);
}

/** Shared Apex zoom config for category x-axis charts (selection + mouse wheel). */
export function buildCategoryZoomChartEvents(
  dataPointCount: number,
  onZoomRangeChange: (range: CategoryZoomRange) => void
) {
  return {
    zoom: {
      enabled: true,
      type: 'x' as const,
      allowMouseWheelZoom: true,
      autoScaleYaxis: true,
    },
    selection: { enabled: true, type: 'x' as const },
    events: {
      zoomed(
        chartContext: unknown,
        { xaxis }: { xaxis?: { min?: number; max?: number } }
      ) {
        onZoomRangeChange(categoryZoomRangeFromChart(chartContext, xaxis, dataPointCount));
      },
    },
  };
}
